import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'system' | 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

/**
 * Theme preference, persisted to localStorage so the user's choice
 * survives reloads. `system` follows `prefers-color-scheme`.
 *
 * The `applyTheme()` helper is called once on app boot (and whenever
 * the user toggles) to flip the `class="dark"` on `<html>` — which is
 * what `tailwind darkMode: 'class'` looks at.
 */
export const useTheme = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'system',
      setTheme: (t) => {
        applyTheme(t);
        set({ theme: t });
      },
    }),
    { name: 'knowlez-theme' },
  ),
);

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  let resolved: 'light' | 'dark';
  if (theme === 'system') {
    resolved = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } else {
    resolved = theme;
  }
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

/** Mount once at app root to honor the persisted preference + watch
 *  for OS-level theme changes when on 'system'. */
export function bindThemeWatcher(): () => void {
  const { theme } = useTheme.getState();
  applyTheme(theme);

  if (!window.matchMedia) return () => undefined;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    const current = useTheme.getState().theme;
    if (current === 'system') applyTheme('system');
  };
  mq.addEventListener?.('change', onChange);
  return () => mq.removeEventListener?.('change', onChange);
}
